import { useCallback, useMemo, useRef, useState, type VoidFunctionComponent } from 'react';
import type { RouteComponent } from 'react-router';

import { Status } from '../../../shared/builds/types';
import { FULL_SECURITY, UNSAFE_HIDE_UNKNOWN_DEPS_BANNER_IN_REPORTS } from '../../../shared/featureFlags/flagNames';
import { useParams, useQuery } from '../../contexts/RouterContext';
import { useIsFeatureEnabled } from '../../helpers/features/featureFlags';
import { stripLastPathSegment } from '../../helpers/paths';
import { useDependenciesCount } from '../../hooks/api/dependencies/useDependenciesCount';
import useRevision from '../../hooks/api/revisions/useRevision';
import { nuc } from '../../utils';
import { Divider } from '../generic/Divider';
import Redirect from '../generic/Redirect';
import SidebarNavLayout from '../layouts/SidebarNavLayout';
import {
  AnalyzedWithoutSparkleCard,
  shouldShowAnalyzedWithoutSparkleCard,
} from '../projects/AnalyzedWithoutSparkleCard/AnalyzedWithoutSparkleCard';
import { useCanDisplaySecurityOptions } from '../projects/generic/hooks';

import { FormatSelector } from './components/FormatSelector';
import { GenerateReportButtonContainer as GenerateReportButton } from './components/GenerateReportButton';
import { Heading } from './components/Heading';
import { ReportFlowSelector } from './components/ReportFlowSelector';
import { StepHeading } from './components/StepHeading';
import { UnknownDependenciesBanner } from './components/UnknownDependenciesBanner';
import { useIsRemediationEnabled } from './hooks/useIsRemediationEnabled';
import { useReportType } from './hooks/useReportType';
import PreviewFiltersLayout from './layout/PreviewFiltersLayout';
import { ReportOptions } from './options';
import { Preview } from './preview';
import { RemediationGuidancePage } from './remediation/RemediationGuidancePage';
import { isReleaseGroupScope, type ReportsV3Params, type ReportsV3QueryParams, type ReportType } from './types';
import { VulnerabilityPage } from './vulnerability/VulnerabilityPage';

/**
 * Container component for the new Reports "V3"/Reports RewRite flow.
 *
 * This features an updated workflow for generating reports, allowing users
 * to either generate a "standard" report (based on what FOSSA recommends for them)
 * or a "custom" report (based on their own preferences).
 *
 * This component handles scope (ie: "is this a project or release group"),
 * and acts as a parent/wrapper for the child paths, which serve as the report types:
 *
 * - licensing
 * - sbom
 * - vulnerability
 *
 * Note that as part of this feature, report settings are moved to the project/release group settings page.
 */
export const ReportsV3Container: RouteComponent = ({ children, location }) => {
  const reportType = useReportType();

  // The URL pathname is always going to end with /reports/:reportType
  // This strips the trailing :reportType param so that we can use it as a base URL for the sidebar nav.
  const baseURL = stripLastPathSegment(location.pathname);

  // Permissions and feature flags.
  const isFullSecurityEnabled = useIsFeatureEnabled(FULL_SECURITY);
  const canDisplayVulnerabilityReport = useCanDisplaySecurityOptions();
  const isRemediationEnabled = useIsRemediationEnabled();

  // This helps us redirect if user tries to access a report type that they don't have access to.
  const shouldRedirect = useMemo(() => {
    // Everyone gets the licensing and sbom reports.
    const allowedReportTypes = new Set<ReportType>(['licensing', 'sbom']);

    if (canDisplayVulnerabilityReport) {
      allowedReportTypes.add('vulnerability');
    }

    if (isRemediationEnabled) {
      allowedReportTypes.add('remediationGuidance');
    }

    return !allowedReportTypes.has(reportType);
  }, [canDisplayVulnerabilityReport, isRemediationEnabled, reportType]);

  if (shouldRedirect) {
    return <Redirect to={`${baseURL}/licensing`} />;
  }

  return (
    <SidebarNavLayout>
      <SidebarNavLayout.Nav>
        <SidebarNavLayout.NavHeading>Reports</SidebarNavLayout.NavHeading>
        <SidebarNavLayout.NavItem to={`${baseURL}/licensing`} isActive={reportType === 'licensing'}>
          Licensing
        </SidebarNavLayout.NavItem>
        <SidebarNavLayout.NavItem to={`${baseURL}/sbom`} isActive={reportType === 'sbom'}>
          SBOM
        </SidebarNavLayout.NavItem>
        {canDisplayVulnerabilityReport && (
          <SidebarNavLayout.NavItem to={`${baseURL}/vulnerability`} isActive={reportType === 'vulnerability'}>
            Vulnerabilities
          </SidebarNavLayout.NavItem>
        )}
        {isRemediationEnabled && (
          <SidebarNavLayout.PaidNavItem
            to={`${baseURL}/remediationGuidance`}
            isActive={reportType === 'remediationGuidance'}
            isRestricted={!isFullSecurityEnabled}
          >
            Remediation Guidance
          </SidebarNavLayout.PaidNavItem>
        )}
      </SidebarNavLayout.Nav>
      <SidebarNavLayout.Content>{children}</SidebarNavLayout.Content>
    </SidebarNavLayout>
  );
};

/**
 * The actual page content for the Reports V3 page. This would be one of the child
 * components of the ReportsV3Container, ie: the licensing, sbom, or vulnerability report.
 */
export const ReportsV3Page: VoidFunctionComponent = () => {
  const reportType = useReportType();
  const [savedOptionsBypassRequestId, setSavedOptionsBypassRequestId] = useState(0);

  const params = useParams<ReportsV3Params>();
  const [query] = useQuery<ReportsV3QueryParams>();
  const isReleaseGroup = isReleaseGroupScope(params);
  const revisionLocator = isReleaseGroup ? '' : `${params.project_id}$${params.revision_id}`;

  // When the custom flow excludes unknown deps from the report, the banner's
  // "in this report" claim no longer holds, so it is hidden entirely.
  const isExcludingUnknownDependencies = query.excludeUnknownDependencies === 'true';

  // On-prem escape hatch (CORE-7479): deployments where most unknown dependencies are
  // proprietary code we can never resolve can hide the banner entirely.
  const isBannerHiddenByConfig = useIsFeatureEnabled(UNSAFE_HIDE_UNKNOWN_DEPS_BANNER_IN_REPORTS);

  const revision = useRevision(revisionLocator, !!revisionLocator);
  const doesRevisionHaveSparkleAnalysis = !!revision.data?.metadata?.latestSparkleAnalysis;
  const revisionAnalyzedAt = revision.data?.dependency_cache_valid;

  // Count with the same status filter the CTA's landing page uses, so the
  // banner number always matches what the user sees after clicking through.
  // Skip the fetch for report types that early-return before the banner renders.
  const shouldFetchUnknownDependencies =
    !!revisionLocator
    && !isBannerHiddenByConfig
    && reportType !== 'vulnerability'
    && reportType !== 'remediationGuidance';
  const unknownDependencies = useDependenciesCount(
    revisionLocator,
    { status: [Status.Unknown] },
    shouldFetchUnknownDependencies,
  );
  const unknownDependencyCount = unknownDependencies.data?.count ?? 0;
  // Project scope only: the banner's CTA is the Manual Dependencies flow
  // (add/overwrite), which is not available on the release-group dependencies page.
  const unknownDependenciesUrl = isReleaseGroup
    ? null
    : `/projects/${nuc(params.project_id)}/refs/${params.reference_type}/${nuc(params.reference)}/${nuc(params.revision_id)}/browse/dependencies?status=unknown`;

  // The ref and callback live here because ReportFlowSelector (Step 1) needs to
  // scroll to StepHeading (Step 2) on flow selection — the parent is the nearest
  // common ancestor, so it owns the ref and threads it to both siblings.
  const step2Ref = useRef<HTMLElement>(null);
  const onSavedOptionsApply = useCallback(() => {
    setSavedOptionsBypassRequestId((currentId) => currentId + 1);
  }, []);
  const scrollToStep2 = () => {
    step2Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    document.getElementById('report-format-input')?.focus({ preventScroll: true });
  };

  // @deprecated The Security/Vulnerability report is a legacy surface gated by
  // the `SECURITY_PDF_REPORTS` feature flag. This report format is no longer
  // supported and is being phased out — do not add new functionality to the
  // VulnerabilityPage or its hooks. New reporting work should target the
  // Remediation Guidance report (or successor) instead.
  if (reportType === 'vulnerability') {
    return <VulnerabilityPage />;
  }

  if (reportType === 'remediationGuidance') {
    return <RemediationGuidancePage />;
  }

  // TODO: we add a bit of padding here to match the mocks better, but it would good to fix this properly.
  // See: https://fossa.atlassian.net/browse/CORE-6467
  return (
    <main className="padding-left-sm">
      {!isReleaseGroup && !doesRevisionHaveSparkleAnalysis
        && shouldShowAnalyzedWithoutSparkleCard(revisionAnalyzedAt) && (
        <div className="margin-bottom-md">
          <AnalyzedWithoutSparkleCard revisionAnalyzedAt={revisionAnalyzedAt} />
        </div>
      )}
      <Heading />
      <StepHeading step={1}>
        <ReportFlowSelector onFlowSelect={scrollToStep2} />
      </StepHeading>
      <StepHeading step={2} ref={step2Ref}>
        <PreviewFiltersLayout>
          <PreviewFiltersLayout.Preview>
            <Preview savedOptionsBypassRequestId={savedOptionsBypassRequestId} />
          </PreviewFiltersLayout.Preview>
          <PreviewFiltersLayout.Filters>
            <div>
              {/* This <div> is needed for spacing/grouping reasons */}
              <FormatSelector />
              <GenerateReportButton />
              {unknownDependenciesUrl && unknownDependencyCount > 0 && !isExcludingUnknownDependencies
              && !isBannerHiddenByConfig && (
              <div className="margin-top-md">
                <UnknownDependenciesBanner count={unknownDependencyCount} dependenciesUrl={unknownDependenciesUrl} />
              </div>
              )}
            </div>
            <Divider />
            <ReportOptions onSavedOptionsApply={onSavedOptionsApply} />
          </PreviewFiltersLayout.Filters>
        </PreviewFiltersLayout>
      </StepHeading>
    </main>
  );
};
